/**
 * @author taulmax
 * @date 2026.03.05
 * @description 일자별 판매량 통계 테이블 컴포넌트
 */
import React from 'react';
import { IDailySalesSummary, IDailySalesDetail } from '../../../../store/useSalesStatistics';

const PAGE_SIZE_OPTIONS = [
  { value: 20, label: '20건' },
  { value: 50, label: '50건' },
  { value: 100, label: '100건' },
  { value: 10000, label: '전체' },
];

interface IDailySalesTableProps {
  dailySales: IDailySalesSummary[];
  dailySalesLoading: boolean;
  dailySalesPage: { totalElements: number; totalPages: number; number: number } | null;
  dailySalesDetails: Record<string, IDailySalesDetail[]>;
  dailySalesDetailLoading: Record<string, boolean>;
  dailySummary: {
    totalFranchiseCount: number;
    totalOrderCount: number;
    totalOrderAmount: number;
  };
  expandedDates: Set<string>;
  toggleDateExpansion: (e: React.MouseEvent, date: string) => void;
  toggleAllDailyExpansion: (e: React.MouseEvent) => void;
  dailyPage: number;
  dailyTotalPages: number;
  dailyPageNumbers: number[];
  handleDailyPageChange: (page: number) => void;
  handleDailyPageSizeChange: (size: number) => void;
  dailyPageSize: number;
}

const DailySalesTable = ({
  dailySales,
  dailySalesLoading,
  dailySalesPage,
  dailySalesDetails,
  dailySalesDetailLoading,
  dailySummary,
  expandedDates,
  toggleDateExpansion,
  toggleAllDailyExpansion,
  dailyPage,
  dailyTotalPages,
  dailyPageNumbers,
  handleDailyPageChange,
  handleDailyPageSizeChange,
  dailyPageSize,
}: IDailySalesTableProps) => {
  return (
    <>
      <div className="!flex !justify-between !items-center !mb-4">
        <h3 className="!text-lg !font-semibold !text-gray-800">
          일자별 판매량 통계
          {dailySalesPage && (
            <span className="!text-sm !font-normal !text-gray-500 !ml-2">
              (총 {dailySalesPage.totalElements}일)
            </span>
          )}
        </h3>
        <select
          value={dailyPageSize}
          onChange={e => handleDailyPageSizeChange(Number(e.target.value))}
          className="!px-3 !py-1.5 !rounded !border !border-gray-300 !text-sm !text-gray-700 !bg-white hover:!border-gray-400 focus:!outline-none focus:!border-blue-500"
        >
          {PAGE_SIZE_OPTIONS.map(({ value, label }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>

      {dailySalesLoading ? (
        <div className="!text-center !py-8 !text-gray-500">
          로딩중...
        </div>
      ) : dailySales.length > 0 ? (
        <>
          <div className="!overflow-x-auto">
            <table className="!w-full !text-sm">
              <thead>
                <tr className="!bg-gray-50 !border-b !border-gray-200">
                  <th className="!text-left !px-4 !py-3 !font-semibold !text-gray-700">
                    <button
                      onClick={toggleAllDailyExpansion}
                      className="!flex !items-center !gap-1 hover:!text-blue-600 !transition-colors"
                    >
                      <svg
                        className={`!w-4 !h-4 !transition-transform ${
                          expandedDates.size === dailySales.length
                            ? '!rotate-90'
                            : ''
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                      날짜
                    </button>
                  </th>
                  <th className="!text-right !px-4 !py-3 !font-semibold !text-gray-700">
                    가맹점 수
                  </th>
                  <th className="!text-right !px-4 !py-3 !font-semibold !text-gray-700">
                    주문건수
                  </th>
                  <th className="!text-right !px-4 !py-3 !font-semibold !text-gray-700">
                    판매금액
                  </th>
                </tr>
              </thead>
              <tbody>
                {dailySales.map((stat, index) => {
                  const isExpanded = expandedDates.has(stat.createdAt);
                  const details = dailySalesDetails[stat.createdAt];
                  const isDetailLoading = dailySalesDetailLoading[stat.createdAt];

                  return (
                    <React.Fragment key={`daily-${index}`}>
                      <tr
                        className="!border-b !border-gray-200 !bg-gray-50 hover:!bg-gray-100 !cursor-pointer"
                        onClick={e => toggleDateExpansion(e, stat.createdAt)}
                      >
                        <td className="!px-4 !py-3 !font-medium !text-gray-900">
                          <div className="!flex !items-center !gap-2">
                            <svg
                              className={`!w-4 !h-4 !transition-transform ${
                                isExpanded ? '!rotate-90' : ''
                              }`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                            {stat.createdAt}
                          </div>
                        </td>
                        <td className="!px-4 !py-3 !text-right !text-gray-700">
                          {(stat.amount.FRANCHISE_COUNT || 0).toLocaleString()}개
                        </td>
                        <td className="!px-4 !py-3 !text-right !text-blue-600 !font-medium">
                          {(stat.amount.ORDER_COUNT || 0).toLocaleString()}건
                        </td>
                        <td className="!px-4 !py-3 !text-right !text-amber-600 !font-medium">
                          {(stat.amount.ORDER_AMOUNT || 0).toLocaleString()}원
                        </td>
                      </tr>
                      {isExpanded && (
                        isDetailLoading ? (
                          <tr key={`daily-${index}-loading`}>
                            <td colSpan={4} className="!px-4 !py-3 !text-center !text-gray-400">
                              상세 로딩중...
                            </td>
                          </tr>
                        ) : details && details.length > 0 ? (
                          details.map((detail, detailIndex) => (
                            <tr
                              key={`daily-${index}-${detailIndex}`}
                              className="!border-b !border-gray-100 hover:!bg-gray-50"
                            >
                              <td className="!px-4 !py-3 !pl-12 !text-gray-500 !text-xs !font-mono">
                                {detail.franchiseId || '-'}
                              </td>
                              <td className="!px-4 !py-3 !text-right !text-gray-700">
                                {detail.companyName}
                              </td>
                              <td className="!px-4 !py-3 !text-right !text-blue-600">
                                {(detail.amount.ORDER_COUNT || 0).toLocaleString()}건
                              </td>
                              <td className="!px-4 !py-3 !text-right !text-amber-600">
                                {(detail.amount.ORDER_AMOUNT || 0).toLocaleString()}원
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr key={`daily-${index}-empty`}>
                            <td colSpan={4} className="!px-4 !py-3 !text-center !text-gray-400">
                              상세 데이터가 없습니다.
                            </td>
                          </tr>
                        )
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="!bg-gray-100 !border-t-2 !border-gray-300">
                  <td className="!px-4 !py-3 !font-bold !text-gray-800 !text-right">
                    합계
                  </td>
                  <td className="!px-4 !py-3 !text-right !font-bold !text-gray-700">
                    {dailySummary.totalFranchiseCount.toLocaleString()}개
                  </td>
                  <td className="!px-4 !py-3 !text-right !font-bold !text-blue-700 !text-base">
                    {dailySummary.totalOrderCount.toLocaleString()}건
                  </td>
                  <td className="!px-4 !py-3 !text-right !font-bold !text-amber-700 !text-base">
                    {dailySummary.totalOrderAmount.toLocaleString()}원
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* 페이지네이션 */}
          {dailyTotalPages > 1 && (
            <div className="!flex !justify-center !items-center !gap-1 !mt-6">
              <button
                onClick={() => handleDailyPageChange(0)}
                disabled={dailyPage === 0}
                className="!px-3 !py-2 !rounded !text-sm !font-medium !transition-colors disabled:!text-gray-300 disabled:!cursor-not-allowed !text-gray-600 hover:!bg-gray-100"
              >
                &laquo;
              </button>
              <button
                onClick={() => handleDailyPageChange(dailyPage - 1)}
                disabled={dailyPage === 0}
                className="!px-3 !py-2 !rounded !text-sm !font-medium !transition-colors disabled:!text-gray-300 disabled:!cursor-not-allowed !text-gray-600 hover:!bg-gray-100"
              >
                &lsaquo;
              </button>
              {dailyPageNumbers.map(page => (
                <button
                  key={page}
                  onClick={() => handleDailyPageChange(page)}
                  className={`!px-3 !py-2 !rounded !text-sm !font-medium !transition-colors !min-w-[36px] ${
                    dailyPage === page
                      ? '!bg-blue-500 !text-white'
                      : '!text-gray-600 hover:!bg-gray-100'
                  }`}
                >
                  {page + 1}
                </button>
              ))}
              <button
                onClick={() => handleDailyPageChange(dailyPage + 1)}
                disabled={dailyPage >= dailyTotalPages - 1}
                className="!px-3 !py-2 !rounded !text-sm !font-medium !transition-colors disabled:!text-gray-300 disabled:!cursor-not-allowed !text-gray-600 hover:!bg-gray-100"
              >
                &rsaquo;
              </button>
              <button
                onClick={() => handleDailyPageChange(dailyTotalPages - 1)}
                disabled={dailyPage >= dailyTotalPages - 1}
                className="!px-3 !py-2 !rounded !text-sm !font-medium !transition-colors disabled:!text-gray-300 disabled:!cursor-not-allowed !text-gray-600 hover:!bg-gray-100"
              >
                &raquo;
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="!text-center !py-8 !text-gray-500">
          조회된 일자별 통계 데이터가 없습니다.
        </div>
      )}
    </>
  );
};

export default DailySalesTable;
